/**
 * Arabic catalogue — the source of truth for all site copy.
 *
 * This is written as Arabic, not as a translation of English. Sentence rhythm,
 * idiom and ordering follow Arabic norms. The English catalogue is derived from
 * this one, not the other way round.
 *
 * Honesty rules encoded here (see docs/ATHKYA_REBRAND_AUDIT.md §8, Decision 1):
 *   - Cloud mode is labelled "قريبًا". It is not claimed to work today.
 *   - Offline is described as failing closed, because that is what it must do.
 *   - No capability is described in the present tense unless it is real.
 */

export const ar = {
  meta: {
    localeName: "العربية",
    dir: "rtl",
  },

  brand: {
    company: "ATHKYA",
    product: "ذكي",
    tagline: "ذكاء يعمل معك، بالطريقة التي تناسبك.",
    logoAlt: "ATHKYA",
  },

  cta: {
    primary: "جرّب ذكي",
    secondary: "شاهد كيف يعمل",
    signIn: "تسجيل الدخول",
    signUp: "إنشاء حساب",
    contact: "تواصل معنا",
    earlyAccess: "انضم للوصول المبكر",
    backHome: "العودة للرئيسية",
    /**
     * Shown in place of `primary` until the platform is live. Says what the
     * button will actually do, rather than promising a product that is not
     * there yet.
     */
    waitlist: "احجز مكانك",
    waitlistSubject: "أريد الانضمام لقائمة انتظار ذكي",
    /** Nowhere to send anyone. A configuration mistake, shown as one. */
    unavailable: "التسجيل غير متاح حاليًا",
  },

  nav: {
    features: "المزايا",
    howItWorks: "كيف يعمل",
    modes: "أوضاع المعالجة",
    security: "الأمان والخصوصية",
    pricing: "الأسعار",
    contact: "تواصل",
    openMenu: "فتح القائمة",
    closeMenu: "إغلاق القائمة",
    switchLanguage: "التبديل إلى الإنجليزية",
  },

  hero: {
    eyebrow: "من ATHKYA",
    title: "ذكاء يعمل معك، بالطريقة التي تناسبك.",
    body: "ذكي ليس نافذة محادثة. إنه فريق من الموظفين الأذكياء يفهم لهجتك، ويعمل على جهازك إن أردت، ولا يخطو خطوة دون إذنك.",
    trust: {
      offline: "يعمل دون اتصال",
      onDevice: "بياناتك على جهازك",
      arabicFirst: "عربي أولًا، لا ترجمة",
    },
  },

  preview: {
    label: "لمحة من مساحة عمل ذكي",
    workspace: "مساحة عمل شركة النخبة",
    nav: {
      home: "الرئيسية",
      assistant: "مساعد ذكي",
      boardroom: "غرفة المجلس",
      projects: "المشاريع",
      agents: "الوكلاء والأقسام",
      files: "الملفات والمعرفة",
      tasks: "المهام",
    },
    modeLabel: "وضع المعالجة",
    modeValue: "محلي",
    modeNote: "لا شيء يغادر هذا الجهاز",
    conversation: {
      user: "جهّز لي خطة إطلاق المنتج الجديد للربع القادم.",
      assistant:
        "فهمت. سأحوّل هذا إلى موجز مشروع، ثم أدعو المختصين لمناقشته قبل التنفيذ.",
      step1: "حوّلت الطلب إلى موجز مشروع",
      step2: "رشّحت أربعة وكلاء للاجتماع",
      step3: "بانتظار موافقتك للبدء",
    },
    composer: "اسأل ذكي أي شيء — بالعربية أو الإنجليزية…",
  },

  how: {
    title: "كيف يعمل ذكي",
    subtitle:
      "أربع خطوات واضحة. ترى كل خطوة وأنت تملك قرار المضي في كل واحدة منها.",
    steps: {
      describe: {
        title: "صِف",
        body: "اكتب ما تريده بالعربية أو الإنجليزية، بلهجتك الطبيعية. لا صياغة خاصة ولا أوامر تحفظها.",
      },
      boardroom: {
        title: "غرفة المجلس",
        body: "يجتمع الوكلاء المناسبون حول الطاولة ويناقشون طلبك أمامك، وترى تقدّمهم وخلاصة تفكيرهم.",
      },
      approve: {
        title: "وافق",
        body: "بعد كل جولة تقرر أنت: اعتماد، أو طلب تعديل، أو إيقاف. لا شيء ينفَّذ قبل موافقتك.",
      },
      execute: {
        title: "نفّذ",
        body: "يحوّل مدير المشروع القرار المعتمد إلى مراحل ومهام، ثم يتابع التنفيذ حتى الاكتمال.",
      },
    },
  },

  modes: {
    title: "أنت تختار أين يفكّر ذكي",
    subtitle:
      "أوضاع المعالجة ليست إعدادًا مخفيًا. هي قرار تتخذه أنت، ويظهر أمامك في كل لحظة.",
    autoNote:
      "الوضع التلقائي يختار من بين هذه المسارات وفق سياستك وما هو متاح — ولا يتجاوز حدودك أبدًا.",
    status: {
      available: "في الوصول المبكر",
      comingSoon: "لاحقًا",
    },
    cloud: {
      name: "سحابي",
      body: "المعالجة على خوادم ATHKYA عبر بوابتنا. لا تزوّدنا بأي مفاتيح، ولا تدير أي مزوّد بنفسك.",
      requirement: "يتطلب اتصالًا بالإنترنت.",
    },
    hybrid: {
      name: "هجين — المحلي أولًا",
      body: "يحاول ذكي المعالجة على جهازك أولًا. لا يخرج إلى السحابة إلا حين تسمح سياستك بذلك صراحةً.",
      requirement: "يحتاج نموذجًا محليًا، والسحابة اختيارية.",
    },
    local: {
      name: "محلي",
      body: "النماذج تعمل على جهازك وحده. لا يغادر نصك الجهاز، ولا تُستدعى أي خدمة خارجية.",
      requirement: "يحتاج نموذجًا محليًا مثبتًا على جهازك.",
    },
    offline: {
      name: "دون اتصال",
      body: "إغلاق آمن بالكامل: لا سحابة، ولا موصّلات، ولا قياسات، ولا مسار بديل. إن لم يكن متاحًا محليًا، يتوقف ذكي ويخبرك — ولا يتسلل إلى الإنترنت.",
      requirement: "يعمل بما هو على جهازك فقط.",
    },
  },

  workforce: {
    title: "فريق، لا أداة واحدة",
    subtitle:
      "تُنظَّم قدرات ذكي في أقسام. لكل وكيل دور محدد وصلاحيات وأدوات وذاكرة خاصة به.",
    note: "أنت تحدد صلاحيات كل قسم، وما يملك الاطلاع عليه، وما لا يملكه.",
    departments: {
      strategy: { name: "الاستراتيجية", role: "يحوّل الفكرة إلى خطة قابلة للقياس" },
      marketing: { name: "التسويق", role: "الرسائل والحملات والمحتوى" },
      finance: { name: "المالية", role: "الميزانيات والتكاليف والتوقعات" },
      operations: { name: "العمليات", role: "الإجراءات والمتابعة والجودة" },
      engineering: { name: "الهندسة", role: "المواصفات والتنفيذ التقني" },
      people: { name: "الموارد البشرية", role: "التوظيف والسياسات والتأهيل" },
    },
  },

  privacy: {
    title: "الخصوصية ليست إعدادًا. إنها الأساس.",
    subtitle:
      "معظم أدوات الذكاء تطلب ثقتك. ذكي مبني ليعمل دون أن يحتاجها — لأن بإمكانه العمل دون أن يغادر جهازك أصلًا.",
    points: {
      failClosed: {
        title: "دون اتصال يعني دون اتصال",
        body: "في هذا الوضع لا يوجد مسار بديل. إن تعذّر التنفيذ محليًا، يتوقف ذكي ويخبرك بوضوح بدل أن يرسل بياناتك إلى الخارج بصمت.",
      },
      onDevice: {
        title: "بياناتك حيث تضعها",
        body: "ملفاتك ومحادثاتك وذاكرة العمل تبقى على جهازك في الأوضاع المحلية. لا نسخة عندنا لأننا ببساطة لا نستلمها.",
      },
      visible: {
        title: "مؤشرات يفهمها الجميع",
        body: "تعرف في كل لحظة أين تجري المعالجة وما الذي غادر جهازك — بلغة واضحة، لا مصطلحات تقنية.",
      },
      noKeys: {
        title: "لا مفاتيح ولا إعدادات مزوّدين",
        body: "لن نطلب منك مفاتيح واجهات برمجية ولا اختيار مزوّد. ترى ذكي ووضع المعالجة، وهذا كل ما يعنيك.",
      },
    },
  },

  boardroom: {
    title: "غرفة المجلس",
    subtitle:
      "بدل إجابة واحدة من صوت واحد، يجلس المختصون حول الطاولة ويناقشون طلبك أمامك — ثم ينتظرون كلمتك.",
    liveLabel: "جلسة جارية",
    roundLabel: "الجولة",
    ofRounds: "من ٣",
    approvalPrompt: "بانتظار قرارك قبل الجولة التالية",
    actions: {
      approve: "اعتماد",
      revise: "طلب تعديل",
      stop: "إيقاف",
    },
    agents: {
      architect: { name: "مهندس الطلب", status: "حوّل الطلب إلى موجز مشروع" },
      strategy: { name: "الاستراتيجية", status: "يحدد المؤشرات والمخاطر" },
      marketing: { name: "التسويق", status: "يقترح الرسالة والقنوات" },
      finance: { name: "المالية", status: "يراجع الكلفة والجدوى" },
      director: { name: "مدير المشروع", status: "بانتظار الاعتماد" },
    },
    summaryLabel: "خلاصة الجولة",
    summary:
      "اتفق الوكلاء على إطلاق تدريجي على ثلاث مراحل، مع تركيز الإنفاق على القناة الأعلى عائدًا. المالية تطلب سقفًا واضحًا قبل المرحلة الثانية.",
  },

  finalCta: {
    title: "ابدأ مع ذكي",
    body: "أنشئ مساحتك — لك أو لشركتك — واختر كيف ومتى وأين يعمل ذكي.",
    note: "الوصول المبكر مفتوح الآن.",
  },

  pages: {
    features: {
      title: "المزايا",
      subtitle: "ما الذي يجعل ذكي مختلفًا عن نافذة محادثة.",
    },
    security: {
      title: "الأمان والخصوصية",
      subtitle: "كيف نتعامل مع بياناتك، وما الذي لا نستلمه أصلًا.",
      commitmentsTitle: "التزاماتنا",
      commitments: {
        one: "لا تُرسل بياناتك إلى أي جهة خارجية في الأوضاع المحلية ودون اتصال.",
        two: "وضع دون اتصال يفشل مغلقًا: لا مسار بديل ولا محاولة صامتة للاتصال.",
        three: "مفاتيح المزوّدين لا توجد في متصفحك ولا على جهازك إطلاقًا.",
        four: "عزل كامل بين مساحات العمل، وصلاحيات محددة لكل عضو.",
        five: "سجل تدقيق لما جرى، ومؤشر واضح لما غادر الجهاز.",
      },
      statusTitle: "ما هو جاهز اليوم",
      statusNote:
        "نفرّق بوضوح بين ما يعمل الآن وما هو قيد التطوير. لن نصف ميزة غير جاهزة بصيغة الحاضر.",
    },
    how: {
      title: "كيف يعمل ذكي",
      subtitle: "من الفكرة إلى التنفيذ، خطوة بخطوة.",
    },
    modes: {
      title: "أوضاع المعالجة",
      subtitle: "خمسة خيارات صريحة، وأنت من يقرر.",
    },
    pricing: {
      title: "الأسعار",
      subtitle: "لم نعلن الأسعار بعد.",
      body: "ذكي في مرحلة الوصول المبكر. نفضّل أن نحدد التسعير بعد أن نفهم كيف تستخدمه فعلًا، بدل أن نفرض رقمًا الآن ونغيّره لاحقًا.",
      earlyAccessTitle: "الوصول المبكر",
      earlyAccessBody:
        "انضم الآن وستحصل على الوصول قبل الإطلاق العام، وسنأخذ ملاحظاتك في الحسبان عند تحديد الخطط.",
    },
    contact: {
      title: "تواصل معنا",
      subtitle: "للاستفسارات والشراكات والوصول المبكر.",
      emailLabel: "البريد الإلكتروني",
    },
    notFound: {
      title: "الصفحة غير موجودة",
      subtitle: "الرابط الذي فتحته لا يقود إلى صفحة.",
    },
  },

  platformNotice: {
    title: "لم تُضبط وجهة المنصة",
    body: "لم تُضبط قيمة VITE_PLATFORM_APP_URL في هذه النسخة، لذا لن تعمل أزرار الدخول والتسجيل. هذه رسالة تطوير ولا تظهر في نسخة الإنتاج المضبوطة.",
  },

  footer: {
    tagline: "ذكاء يعمل معك، بالطريقة التي تناسبك.",
    product: "المنتج",
    company: "الشركة",
    legal: "قانوني",
    privacy: "سياسة الخصوصية",
    terms: "شروط الاستخدام",
    rights: "جميع الحقوق محفوظة.",
    /**
     * Shown when `VITE_CONTACT_EMAIL` is not set. Deliberately conspicuous: a
     * build that reaches a visitor without a contact address is a mistake, and
     * it should look like one rather than quietly showing nothing.
     */
    emailUnset: "لم يُضبط بريد التواصل بعد",
  },

  legal: {
    updated: "آخر تحديث",
    /**
     * Shown at the top of both documents. Deliberately prominent rather than
     * buried: these describe real data handling accurately, but they have not
     * been through legal review, and a reader deserves to know that before
     * relying on them.
     */
    draftNotice:
      "هذه المستندات تصف ممارسات ATHKYA الفعلية بدقة، لكنها لم تُراجع قانونيًا بعد. النسخة النهائية ستُنشر قبل توفّر الخدمة للعموم.",

    privacy: {
      title: "سياسة الخصوصية",
      intro:
        "ذكي مبني على قاعدة واحدة: بياناتك ملكك. هذه السياسة تشرح ما نجمعه فعلًا — والأهم، ما لا نجمعه.",

      localTitle: "حيث تعيش بياناتك",
      localBody:
        "محادثاتك وملفاتك ومعرفتك تُخزَّن على جهازك في قاعدة بيانات محلية. لا نرفعها إلى خوادمنا، ولا نحتفظ بنسخة منها، ولا نستطيع قراءتها. وإن حذفت التطبيق حُذفت معه.",

      modesTitle: "أوضاع المعالجة وما يعنيه كل وضع",
      modesBody:
        "الوضع الذي تختاره يحدّد ما يترك جهازك. هذا ليس إعدادًا تجميليًا — بل يُنفَّذ في الخادم عند نقاط عبور الشبكة:",
      modeLocal:
        "محلي: تُعالَج الطلبات على جهازك بنموذج محلي. لا يترك نصّك الجهاز.",
      modeOffline:
        "دون اتصال: يُرفض كل اتصال شبكي رفضًا صريحًا — لا نماذج سحابية، ولا موصّلات، ولا قياسات، ولا احتياط. يفشل مغلقًا: إن لم يكن بالإمكان تنفيذ طلب محليًا فسنقول ذلك، لا نتسلّل إلى السحابة.",
      modeCloud:
        "سحابي: يُرسَل نصّ طلبك عبر بوابة ATHKYA إلى مزوّد نماذج لمعالجته وإرجاع الجواب. لا يُستخدم لتدريب أي نموذج.",
      modeHybrid:
        "هجين ومحلي أولًا: يُحاول محليًا أولًا، ولا يخرج إلا لما لا يستطيع الجهاز إنجازه — ويخبرك حين يفعل.",

      keysTitle: "المفاتيح والأسرار",
      keysBody:
        "لا نطلب منك مفاتيح مزوّدين، ولا نقبلها. مفاتيح الوصول إلى النماذج تبقى في الخادم ولا تُرسَل إلى المتصفح ولا تُخزَّن على جهازك. وما تحفظه أنت من بيانات اعتماد لخدماتك يُخزَّن مشفَّرًا محليًا.",

      collectTitle: "ما نجمعه فعلًا",
      collectBody:
        "للموقع: لا نستخدم متعقّبات إعلانية ولا نبيع بيانات لأحد. وللحساب — إن أنشأت واحدًا — نحتفظ بما يلزم لتشغيله: بريدك، وسجلّ الاستخدام للفوترة. وفي وضع «دون اتصال» لا تُرسَل أي قياسات إطلاقًا.",

      rightsTitle: "حقوقك",
      rightsBody:
        "يمكنك الاطلاع على بياناتك وتصديرها وحذفها في أي وقت. ولأن الجزء الأكبر منها على جهازك، فالحذف عندك حذف فعلي لا طلب تنتظر جوابه.",

      contactTitle: "أسئلة",
      contactBody: "لأي سؤال عن الخصوصية راسلنا على:",
    },

    terms: {
      title: "شروط الاستخدام",
      intro:
        "شروط مختصرة ومباشرة. باستخدامك ذكي فأنت توافق على ما يلي.",

      serviceTitle: "الخدمة",
      serviceBody:
        "ذكي مساحة عمل ذكية تُشغّل وكلاء يعملون بالنيابة عنك. أنت تختار وضع المعالجة، والوكلاء لا يتخذون إجراءً ذا أثر خارجي دون موافقتك.",

      accountTitle: "حسابك",
      accountBody:
        "أنت مسؤول عن حماية حسابك وعن كل ما يجري عبره. أخبرنا فورًا إن ظننت أن أحدًا وصل إليه.",

      useTitle: "استخدام مقبول",
      useBody:
        "لا تستخدم ذكي لما يخالف القانون، ولا لإيذاء الآخرين، ولا لمحاولة اختراق الخدمة أو تجاوز حدودها، ولا لانتحال شخصية أحد.",

      aiTitle: "حدود الذكاء الاصطناعي",
      aiBody:
        "الوكلاء قد يخطئون. لا تعتمد على مخرجاتهم في قرار طبي أو قانوني أو مالي دون مراجعة مختص. أنت من يقرّر، والوكلاء يعرضون أسبابهم لتحكم عليها.",

      contentTitle: "ما تُنشئه",
      contentBody:
        "ما تُدخله وما يُنتَج لك ملكك. لا نطالب بحقوق فيه ولا نستخدمه لتدريب النماذج.",

      openSourceTitle: "المصادر المفتوحة",
      openSourceBody:
        "ذكي مبني على برمجيات مفتوحة المصدر، وتراخيصها وإسناداتها محفوظة كما تقتضيه شروطها.",

      changesTitle: "تغيّر الشروط",
      changesBody:
        "قد نحدّث هذه الشروط. وسنخبرك بالتغييرات الجوهرية قبل نفاذها لا بعده.",

      contactTitle: "تواصل",
      contactBody: "لأي سؤال عن هذه الشروط:",
    },
  },
} as const;

/**
 * Widen the literal types produced by `as const` so other locales can supply
 * their own strings while still being checked for exact key parity.
 */
type Localized<T> = {
  [K in keyof T]: T[K] extends string ? string : Localized<T[K]>;
};

export type Catalog = Localized<typeof ar>;
